import { useTranslation } from '@jq4race/i18n';
import type { MemberResponse } from '@jq4race/shared';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
} from '@jq4race/ui';
import { useEffect, useMemo, useState } from 'react';
import { translateApiError } from '../../../../shared/errors/translateApiError';

// frequent-customer-memberships · Slice S2a — spec Capability 2, "Plate
// reassignment is supported". Moves a plate from its current member to a
// different member without ever producing a duplicate active record.

interface ReassignVehicleDialogProps {
  open: boolean;
  members: MemberResponse[];
  onConfirm: (plate: string, toMemberId: string) => Promise<void>;
  onCancel: () => void;
}

export function ReassignVehicleDialog({
  open,
  members,
  onConfirm,
  onCancel,
}: ReassignVehicleDialogProps) {
  const { t } = useTranslation('memberships');
  const plateOptions = useMemo(
    () => members.flatMap((m) => m.vehicles.map((v) => ({ plate: v.plate, ownerName: m.name }))),
    [members],
  );
  const [plate, setPlate] = useState('');
  const [toMemberId, setToMemberId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setPlate(plateOptions[0]?.plate ?? '');
      setToMemberId('');
      setError(null);
    }
  }, [open, plateOptions]);

  async function confirm() {
    if (!plate || !toMemberId) {
      setError(t('reassignVehicleDialog.selectionRequired'));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm(plate, toMemberId);
      onCancel();
    } catch (e) {
      setError(translateApiError(e, t, { fallback: t('reassignVehicleDialog.genericError') }));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => (!next ? onCancel() : undefined)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('reassignVehicleDialog.title')}</DialogTitle>
          <DialogDescription>{t('reassignVehicleDialog.description')}</DialogDescription>
        </DialogHeader>

        <div className="mt-4 space-y-4">
          <div>
            <Label htmlFor="reassign-plate" className="mb-1.5 block text-[13px] font-medium">
              {t('reassignVehicleDialog.plateLabel')}
            </Label>
            <select
              id="reassign-plate"
              value={plate}
              onChange={(e) => setPlate(e.target.value)}
              disabled={submitting}
              className="h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-[13px] shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
            >
              {plateOptions.length === 0 && (
                <option value="">{t('reassignVehicleDialog.noPlatesRegistered')}</option>
              )}
              {plateOptions.map((opt) => (
                <option key={opt.plate} value={opt.plate}>
                  {t('reassignVehicleDialog.plateOption', {
                    plate: opt.plate,
                    ownerName: opt.ownerName,
                  })}
                </option>
              ))}
            </select>
          </div>

          <div>
            <Label htmlFor="reassign-member" className="mb-1.5 block text-[13px] font-medium">
              {t('reassignVehicleDialog.newMemberLabel')}
            </Label>
            <select
              id="reassign-member"
              value={toMemberId}
              onChange={(e) => setToMemberId(e.target.value)}
              disabled={submitting}
              className="h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-[13px] shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
            >
              <option value="">{t('reassignVehicleDialog.selectMemberPlaceholder')}</option>
              {members.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {error && (
          <p className="mt-4 rounded-sm border border-jq-danger/40 bg-jq-danger/10 px-3 py-2 text-[13px] text-jq-danger">
            {error}
          </p>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onCancel}
            disabled={submitting}
          >
            {t('common:actions.cancel')}
          </Button>
          <Button type="button" size="sm" onClick={confirm} disabled={submitting}>
            {submitting ? t('reassignVehicleDialog.saving') : t('reassignVehicleDialog.reassign')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
